import React, { useMemo, useState, useEffect, useContext } from "react";
import {
  FiExternalLink,
  FiShuffle,
  FiSearch,
  FiFileText,
  FiTrash2,
  FiRefreshCw,
  FiRotateCcw,
  FiCheckCircle,
  FiSend,
  FiBarChart2,
  FiDollarSign,
  FiPlusCircle,
} from "react-icons/fi";
import { ThemeContext } from "../../App";
import companies from "../../data/companies";
import { normalizeApply } from "../../utils/applyMerge";
import "./apply.css";

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

// Accepts a bare or fully-qualified URL and returns a canonical https URL, or
// null when it can't be parsed into a real host. "acme.com/jobs" becomes
// "https://acme.com/jobs". An empty input returns "" (nothing entered yet).
function normalizeUrl(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    const u = new URL(withScheme);
    if (!u.hostname || !u.hostname.includes(".")) return null;
    return u.toString();
  } catch {
    return null;
  }
}

// URL-friendly company slug: lowercase, with any run of non-alphanumeric
// characters collapsed into a single hyphen. Matches the slug scheme used by
// both levels.fyi and 6figr (e.g. "Goldman Sachs" -> "goldman-sachs").
function slugifyCompany(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Software-Engineer-in-India salary page for a company on levels.fyi
// (country=113 is India).
function levelsFyiUrl(name) {
  return `https://www.levels.fyi/companies/${slugifyCompany(
    name
  )}/salaries/software-engineer/locations/india?country=113`;
}

// Company salary page on 6figr (India).
function sixFigrUrl(name) {
  return `https://6figr.com/in/salary/${slugifyCompany(name)}`;
}

function letterOf(name) {
  const c = (name || "").trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : "#";
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const EMPTY_TRACK = { applied: false, lastApplied: null, coldDm: false, notes: "" };
const LETTERS = ["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")];

// Tracking controls shared by the random pick card and each browse row.
function TrackControls({ t, onToggleApplied, onAppliedAgain, onToggleColdDm, onSaveNotes, onBlock, isCustom, onRemove }) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [draft, setDraft] = useState(t.notes || "");

  // keep the draft in sync if the underlying note changes (e.g. cloud merge)
  useEffect(() => {
    setDraft(t.notes || "");
  }, [t.notes]);

  return (
    <div className="apply-track">
      <div className="apply-track-row">
        <label className={`apply-check ${t.applied ? "on" : ""}`}>
          <input type="checkbox" checked={t.applied} onChange={onToggleApplied} />
          <FiCheckCircle size={14} />
          <span>Applied</span>
        </label>

        {t.applied ? (
          <>
            {t.lastApplied ? <span className="apply-date">Last applied {fmtDate(t.lastApplied)}</span> : null}
            <button type="button" className="nb-btn nb-btn-sm" onClick={onAppliedAgain} title="Bump last-applied date to today">
              <FiRefreshCw size={13} /> Applied again
            </button>
          </>
        ) : null}

        <label className={`apply-check ${t.coldDm ? "on" : ""}`}>
          <input type="checkbox" checked={t.coldDm} onChange={onToggleColdDm} />
          <FiSend size={13} />
          <span>Cold DM&apos;d</span>
        </label>

        <button type="button" className="nb-btn nb-btn-sm" onClick={() => setNotesOpen((o) => !o)}>
          <FiFileText size={13} /> {t.notes ? "Edit notes" : "Add notes"}
        </button>

        {isCustom ? (
          <button type="button" className="nb-btn nb-btn-sm apply-danger" onClick={onRemove} title="Remove this company you added">
            <FiTrash2 size={13} /> Remove
          </button>
        ) : (
          <button type="button" className="nb-btn nb-btn-sm apply-danger" onClick={onBlock} title="Hide this company forever (you can restore it later)">
            <FiTrash2 size={13} /> Delete forever
          </button>
        )}
      </div>

      {t.notes && !notesOpen ? <div className="apply-note-preview">{t.notes}</div> : null}

      {notesOpen ? (
        <div className="apply-notes">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            placeholder="Referral contact, application status, follow-up date…"
          />
          <div className="apply-notes-actions">
            <button
              type="button"
              className="nb-btn nb-btn-sm"
              onClick={() => {
                setDraft(t.notes || "");
                setNotesOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="nb-btn nb-btn-primary nb-btn-sm"
              onClick={() => {
                onSaveNotes(draft);
                setNotesOpen(false);
              }}
            >
              Save notes
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// "Explore salary" links for a company — opens external compensation data
// (levels.fyi & 6figr) in a new tab. Purely presentational: no state, no API.
function SalaryLinks({ name }) {
  if (!slugifyCompany(name)) return null;
  return (
    <div className="apply-salary">
      <span className="apply-salary-label">
        <FiDollarSign size={13} /> Explore salary
      </span>
      <a
        className="nb-btn nb-btn-sm"
        href={levelsFyiUrl(name)}
        target="_blank"
        rel="noopener noreferrer"
        title={`${name} — Software Engineer salaries in India on levels.fyi`}
      >
        <FiExternalLink size={13} /> levels.fyi
      </a>
      <a
        className="nb-btn nb-btn-sm"
        href={sixFigrUrl(name)}
        target="_blank"
        rel="noopener noreferrer"
        title={`${name} — salaries on 6figr`}
      >
        <FiExternalLink size={13} /> 6figr
      </a>
    </div>
  );
}

// One company in the browse list. Tracking controls are ALWAYS visible so
// every action (Applied / Cold DM / Notes / Delete) is discoverable without
// having to click or hover to expand the row.
function CompanyRow({ c, t, handlers, isCustom, onRemove }) {
  return (
    <li className="apply-row open">
      <div className="apply-row-head">
        <span className="apply-row-name" title={c.name}>{c.name}</span>

        {isCustom ? <span className="nb-pill gray apply-mini">Yours</span> : null}
        {t.applied ? <span className="nb-pill green apply-mini">Applied</span> : null}
        {t.coldDm ? <span className="nb-pill blue apply-mini">Cold DM</span> : null}
        {t.notes ? <span className="nb-pill gray apply-mini">Note</span> : null}

        <span className="apply-row-host">{hostOf(c.url)}</span>
        <a className="nb-btn nb-btn-sm" href={c.url} target="_blank" rel="noopener noreferrer">
          <FiExternalLink size={13} /> Open careers
        </a>
      </div>
      <SalaryLinks name={c.name} />
      <TrackControls t={t} {...handlers} isCustom={isCustom} onRemove={onRemove} />
    </li>
  );
}

// Inline "add a company" control shown at the top of the Browse list. Added
// companies are merged straight into the same company list (Random & Browse) and
// saved to the user's shard, so they behave exactly like the built-in ones — no
// separate section. Managing (removing) a custom company happens inline on its
// row, like any other company.
function AddCompanyInline({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  function submit(e) {
    e.preventDefault();
    const err = onAdd(name, url);
    if (err) {
      setError(err);
      return;
    }
    setName("");
    setUrl("");
    setError("");
    setOpen(false);
  }

  if (!open) {
    return (
      <button type="button" className="apply-addinline-btn" onClick={() => setOpen(true)}>
        <FiPlusCircle size={15} /> Add a company
      </button>
    );
  }

  return (
    <form className="apply-addinline" onSubmit={submit}>
      <input
        className="apply-add-input"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          if (error) setError("");
        }}
        placeholder="Company name (e.g. Acme)"
        aria-label="Company name"
        autoFocus
      />
      <input
        className="apply-add-input"
        value={url}
        onChange={(e) => {
          setUrl(e.target.value);
          if (error) setError("");
        }}
        placeholder="Careers URL (e.g. acme.com/careers)"
        aria-label="Careers URL"
      />
      <button type="submit" className="nb-btn nb-btn-primary nb-btn-sm">
        <FiPlusCircle size={13} /> Add
      </button>
      <button
        type="button"
        className="nb-btn nb-btn-sm"
        onClick={() => {
          setOpen(false);
          setError("");
        }}
      >
        Cancel
      </button>
      {error ? <div className="apply-addinline-error">{error}</div> : null}
    </form>
  );
}

export default function Apply({ applyData = { blocklist: [], tracked: {}, custom: [] }, updateApply = () => {} }) {
  const dark = useContext(ThemeContext);
  const data = useMemo(() => normalizeApply(applyData), [applyData]);
  const blocked = useMemo(() => new Set(data.blocklist), [data.blocklist]);

  const [mode, setMode] = useState("random"); // random | browse
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all"); // all | todo | applied | colddm
  const [showHidden, setShowHidden] = useState(false);

  // Built-in companies plus the user's own additions (their shard). Custom
  // entries whose name already exists in the built-in list are ignored so a
  // company never shows up twice.
  const allCompanies = useMemo(() => {
    const seen = new Set(companies.map((c) => c.name.toLowerCase()));
    const extra = [];
    data.custom.forEach((c) => {
      const key = c.name.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      extra.push(c);
    });
    return [...companies, ...extra];
  }, [data.custom]);

  const visible = useMemo(() => allCompanies.filter((c) => !blocked.has(c.name)), [allCompanies, blocked]);

  // Names the user added themselves (lower-cased), so rows can be tagged "Yours"
  // and offer an inline Remove instead of the built-in "hide forever".
  const customNames = useMemo(() => new Set(data.custom.map((c) => c.name.toLowerCase())), [data.custom]);

  const [pick, setPick] = useState(() => visible[Math.floor(Math.random() * visible.length)] || null);

  const track = (name) => data.tracked[name] || EMPTY_TRACK;

  // ---- mutations (all immutable, routed through updateApply) ----
  const setTrack = (name, patch) => {
    const prev = data.tracked[name] || EMPTY_TRACK;
    updateApply({ ...data, tracked: { ...data.tracked, [name]: { ...prev, ...patch } } });
  };
  const toggleApplied = (name) => {
    const prev = track(name);
    if (prev.applied) setTrack(name, { applied: false });
    else setTrack(name, { applied: true, lastApplied: todayISO() });
  };
  const appliedAgain = (name) => setTrack(name, { applied: true, lastApplied: todayISO() });
  const toggleColdDm = (name) => setTrack(name, { coldDm: !track(name).coldDm });
  const saveNotes = (name, notes) => setTrack(name, { notes });
  const block = (name) => {
    updateApply({ ...data, blocklist: Array.from(new Set([...data.blocklist, name])) });
  };
  const restore = (name) => {
    updateApply({ ...data, blocklist: data.blocklist.filter((n) => n !== name) });
  };

  // Add a user-supplied company to the personal shard. Returns "" on success or
  // a human-readable error string the form shows inline. Adding a name that was
  // previously hidden also un-hides it, so it becomes visible again.
  const addCompany = (rawName, rawUrl) => {
    const name = (rawName || "").trim();
    if (!name) return "Enter a company name.";
    const url = normalizeUrl(rawUrl);
    if (url === null) return "Enter a valid careers URL (e.g. acme.com/careers).";
    if (!url) return "Enter the company's careers URL.";
    if (allCompanies.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      return `“${name}” is already in the list.`;
    }
    updateApply({
      ...data,
      custom: [...data.custom, { name, url }],
      blocklist: data.blocklist.filter((n) => n.toLowerCase() !== name.toLowerCase()),
    });
    return "";
  };

  // Permanently remove a company the user added, along with any tracking/hidden
  // state left over for that name.
  const removeCustom = (name) => {
    const nextTracked = { ...data.tracked };
    delete nextTracked[name];
    updateApply({
      ...data,
      custom: data.custom.filter((c) => c.name !== name),
      tracked: nextTracked,
      blocklist: data.blocklist.filter((n) => n !== name),
    });
  };

  const handlersFor = (name) => ({
    onToggleApplied: () => toggleApplied(name),
    onAppliedAgain: () => appliedAgain(name),
    onToggleColdDm: () => toggleColdDm(name),
    onSaveNotes: (notes) => saveNotes(name, notes),
    onBlock: () => block(name),
  });

  function pickRandom(pool) {
    const list = pool || visible;
    if (list.length === 0) {
      setPick(null);
      return;
    }
    if (list.length === 1) {
      setPick(list[0]);
      return;
    }
    let next = pick;
    while (!next || next === pick || blocked.has(next.name)) {
      next = list[Math.floor(Math.random() * list.length)];
    }
    setPick(next);
  }

  function blockFromRandom(name) {
    const pool = visible.filter((c) => c.name !== name);
    block(name);
    if (pick && pick.name === name) pickRandom(pool);
  }

  // status counts for the filter chips
  const counts = useMemo(() => {
    let applied = 0;
    let colddm = 0;
    let notes = 0;
    visible.forEach((c) => {
      const t = data.tracked[c.name];
      if (t && t.applied) applied++;
      if (t && t.coldDm) colddm++;
      if (t && t.notes && t.notes.trim()) notes++;
    });
    return { all: visible.length, applied, colddm, notes, todo: visible.length - applied };
  }, [visible, data.tracked]);

  // Companies the user has applied to, most-recently-applied first — powers the
  // "My applications" dashboard. Pure client render of existing state, so it
  // adds no API calls.
  const appliedList = useMemo(() => {
    return visible
      .filter((c) => data.tracked[c.name] && data.tracked[c.name].applied)
      .sort((a, b) => {
        const da = (data.tracked[a.name] && data.tracked[a.name].lastApplied) || "";
        const db = (data.tracked[b.name] && data.tracked[b.name].lastApplied) || "";
        return db.localeCompare(da);
      });
  }, [visible, data.tracked]);

  // Cold-DM'd companies the user hasn't applied to yet (a natural follow-up list).
  const coldOnlyList = useMemo(() => {
    return visible.filter(
      (c) => data.tracked[c.name] && data.tracked[c.name].coldDm && !data.tracked[c.name].applied
    );
  }, [visible, data.tracked]);

  const q = query.trim().toLowerCase();
  const browseList = useMemo(() => {
    let list = visible;
    if (status === "todo") list = list.filter((c) => !(data.tracked[c.name] && data.tracked[c.name].applied));
    else if (status === "applied") list = list.filter((c) => data.tracked[c.name] && data.tracked[c.name].applied);
    else if (status === "colddm") list = list.filter((c) => data.tracked[c.name] && data.tracked[c.name].coldDm);
    if (q) list = list.filter((c) => c.name.toLowerCase().includes(q));
    return list;
  }, [visible, status, q, data.tracked]);

  const groups = useMemo(() => {
    const map = {};
    for (const c of browseList) {
      const l = letterOf(c.name);
      (map[l] = map[l] || []).push(c);
    }
    return map;
  }, [browseList]);

  const showAZ = !q && status === "all";

  function jumpTo(letter) {
    const id = `apply-grp-${letter === "#" ? "hash" : letter}`;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const STATUS_TABS = [
    ["all", `All (${counts.all})`],
    ["todo", `To apply (${counts.todo})`],
    ["applied", `Applied (${counts.applied})`],
    ["colddm", `Cold DM (${counts.colddm})`],
  ];

  return (
    <div className={`apply-page ${dark ? "apply-dark" : ""}`}>
      <div className="page-header">
        <div className="page-emoji" role="img" aria-label="briefcase">
          💼
        </div>
        <h1 className="page-title">Apply</h1>
        <p className="page-sub">
          {visible.length} product-based companies &amp; startups that hire beyond FAANG. Pick one at random or browse A–Z,
          then track each application — mark <strong>Applied</strong>, log a <strong>Cold DM</strong>, jot{" "}
          <strong>notes</strong>, or hide ones you&apos;re not interested in.
        </p>
        {counts.applied > 0 ? (
          <button type="button" className="apply-stat apply-stat-link" onClick={() => setMode("dashboard")}>
            <FiCheckCircle size={13} /> {counts.applied} applied
            {blocked.size ? ` · ${blocked.size} hidden` : ""} · view dashboard
          </button>
        ) : null}
      </div>

      <div className="apply-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "random"}
          className={`apply-tab ${mode === "random" ? "active" : ""}`}
          onClick={() => setMode("random")}
        >
          <FiShuffle size={15} /> Random
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "browse"}
          className={`apply-tab ${mode === "browse" ? "active" : ""}`}
          onClick={() => setMode("browse")}
        >
          <FiSearch size={15} /> Browse A–Z
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "dashboard"}
          className={`apply-tab ${mode === "dashboard" ? "active" : ""}`}
          onClick={() => setMode("dashboard")}
        >
          <FiBarChart2 size={15} /> My applications
        </button>
      </div>

      {mode === "random" ? (
        <div className="apply-random">
          {pick ? (
            <div className="apply-pick-card">
              <div className="apply-pick-emoji">💼</div>
              <div className="apply-pick-name">{pick.name}</div>
              {hostOf(pick.url) ? <div className="apply-pick-host">{hostOf(pick.url)}</div> : null}
              <div className="apply-pick-actions">
                <a className="nb-btn nb-btn-primary" href={pick.url} target="_blank" rel="noopener noreferrer">
                  <FiExternalLink size={15} /> Open careers
                </a>
                <button type="button" className="nb-btn" onClick={() => pickRandom()}>
                  <FiShuffle size={15} /> Pick another
                </button>
              </div>
              <SalaryLinks name={pick.name} />
              <TrackControls
                t={track(pick.name)}
                onToggleApplied={() => toggleApplied(pick.name)}
                onAppliedAgain={() => appliedAgain(pick.name)}
                onToggleColdDm={() => toggleColdDm(pick.name)}
                onSaveNotes={(notes) => saveNotes(pick.name, notes)}
                onBlock={() => blockFromRandom(pick.name)}
              />
            </div>
          ) : (
            <div className="apply-empty">No companies left — restore some from the Browse tab.</div>
          )}
          <p className="apply-tip">Tip: every click suggests a different company — great for a daily “apply to one more” habit.</p>
        </div>
      ) : mode === "dashboard" ? (
        <div className="apply-dash">
          <div className="apply-stat-grid">
            <div className="apply-stat-card">
              <span className="apply-stat-num">{counts.applied}</span>
              <span className="apply-stat-lbl">Applied</span>
            </div>
            <div className="apply-stat-card">
              <span className="apply-stat-num">{counts.colddm}</span>
              <span className="apply-stat-lbl">Cold DM&apos;d</span>
            </div>
            <div className="apply-stat-card">
              <span className="apply-stat-num">{counts.notes}</span>
              <span className="apply-stat-lbl">With notes</span>
            </div>
            <div className="apply-stat-card">
              <span className="apply-stat-num">{blocked.size}</span>
              <span className="apply-stat-lbl">Hidden</span>
            </div>
          </div>

          {appliedList.length === 0 ? (
            <div className="apply-empty">
              You haven&apos;t logged any applications yet. Open the <strong>Random</strong> or{" "}
              <strong>Browse A–Z</strong> tab, mark a company <strong>Applied</strong>, and it&apos;ll show up here.
            </div>
          ) : (
            <>
              <div className="apply-dash-head">
                <span className="apply-dash-title">
                  {appliedList.length} compan{appliedList.length === 1 ? "y" : "ies"} applied
                </span>
                <span className="apply-dash-sub">Most recent first</span>
              </div>
              <ul className="apply-rows">
                {appliedList.map((c) => (
                  <CompanyRow key={c.name} c={c} t={track(c.name)} handlers={handlersFor(c.name)} isCustom={customNames.has(c.name.toLowerCase())} onRemove={() => removeCustom(c.name)} />
                ))}
              </ul>
            </>
          )}

          {coldOnlyList.length > 0 ? (
            <>
              <div className="apply-dash-head apply-dash-head-sec">
                <span className="apply-dash-title">Cold DM&apos;d — not applied yet ({coldOnlyList.length})</span>
              </div>
              <ul className="apply-rows">
                {coldOnlyList.map((c) => (
                  <CompanyRow key={c.name} c={c} t={track(c.name)} handlers={handlersFor(c.name)} isCustom={customNames.has(c.name.toLowerCase())} onRemove={() => removeCustom(c.name)} />
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : (
        <div className="apply-browse">
          <div className="apply-browse-top">
            <div className="apply-search">
              <FiSearch size={15} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search companies…" aria-label="Search companies" />
              {query ? (
                <button type="button" className="apply-clear" onClick={() => setQuery("")}>
                  Clear
                </button>
              ) : null}
            </div>
            <AddCompanyInline onAdd={addCompany} />
          </div>

          <div className="apply-statusbar">
            {STATUS_TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`apply-status ${status === key ? "active" : ""}`}
                onClick={() => setStatus(key)}
              >
                {label}
              </button>
            ))}
            {blocked.size ? (
              <button type="button" className={`apply-status apply-status-hidden ${showHidden ? "active" : ""}`} onClick={() => setShowHidden((s) => !s)}>
                Hidden ({blocked.size})
              </button>
            ) : null}
          </div>

          {showHidden && blocked.size ? (
            <div className="apply-hidden">
              <div className="apply-hidden-head">Hidden companies — they never appear in Random or Browse until restored.</div>
              <ul className="apply-rows">
                {data.blocklist.map((name) => (
                  <li key={name} className="apply-row apply-row-hidden">
                    <div className="apply-row-head">
                      <span className="apply-row-name">{name}</span>
                      <button type="button" className="nb-btn nb-btn-sm" onClick={() => restore(name)}>
                        <FiRotateCcw size={13} /> Restore
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {showAZ ? (
            <div className="apply-azbar">
              {LETTERS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className="apply-az"
                  disabled={!groups[l]}
                  onClick={() => jumpTo(l)}
                  title={groups[l] ? `Jump to ${l}` : `No companies under ${l}`}
                >
                  {l}
                </button>
              ))}
            </div>
          ) : null}

          <div className="apply-list">
            {browseList.length === 0 ? (
              <div className="apply-empty">No companies match{q ? ` “${query}”` : " this filter"}.</div>
            ) : !showAZ ? (
              <>
                <div className="apply-count">
                  {browseList.length} compan{browseList.length === 1 ? "y" : "ies"}
                </div>
                <ul className="apply-rows">
                  {browseList.map((c) => (
                    <CompanyRow key={c.name} c={c} t={track(c.name)} handlers={handlersFor(c.name)} isCustom={customNames.has(c.name.toLowerCase())} onRemove={() => removeCustom(c.name)} />
                  ))}
                </ul>
              </>
            ) : (
              LETTERS.filter((l) => groups[l]).map((l) => (
                <div key={l} className="apply-group" id={`apply-grp-${l === "#" ? "hash" : l}`}>
                  <div className="apply-group-head">{l}</div>
                  <ul className="apply-rows">
                    {groups[l].map((c) => (
                      <CompanyRow key={c.name} c={c} t={track(c.name)} handlers={handlersFor(c.name)} isCustom={customNames.has(c.name.toLowerCase())} onRemove={() => removeCustom(c.name)} />
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
